import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import api from '../lib/api';
import { setAuth } from '../lib/auth';
import { Lock, Mail, ArrowRight, ShieldCheck } from 'lucide-react';

function AnimatedBubble({ style }) {
  return <div className="login-bubble" style={style} />;
}

export default function LoginPage({ onLogin }) {
  const [email, setEmail] = useState('');
  const [wachtwoord, setWachtwoord] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [needs2fa, setNeeds2fa] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  useEffect(() => {
    setTimeout(() => setMounted(true), 50);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const payload = { email, wachtwoord };
      if (needs2fa) payload.totp_code = totpCode;
      const res = await api.post('/api/auth/login', payload);
      if (res.data.requires_2fa) {
        setNeeds2fa(true);
        setLoading(false);
        return;
      }
      setAuth(res.data.token, res.data.user);
      onLogin(res.data.user);
    } catch (err) {
      setError(err.response?.data?.detail || 'Inloggen mislukt');
    } finally {
      setLoading(false);
    }
  };

  const bubbles = Array.from({ length: 20 }, (_, i) => ({
    width: `${15 + Math.random() * 45}px`,
    height: `${15 + Math.random() * 45}px`,
    left: `${Math.random() * 100}%`,
    bottom: `-${20 + Math.random() * 40}px`,
    animationDuration: `${8 + Math.random() * 12}s`,
    animationDelay: `${Math.random() * 8}s`,
  }));

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotLoading(true);
    try {
      await api.post('/api/auth/forgot-password', { email: forgotEmail });
      setForgotSent(true);
    } catch { /* always show success */ setForgotSent(true); }
    finally { setForgotLoading(false); }
  };


  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center"
      style={{ background: 'linear-gradient(135deg, #001a2e 0%, #003366 40%, #00244d 70%, #001a38 100%)' }}
    >
      {/* Animated bubbles background */}
      {bubbles.map((style, i) => <AnimatedBubble key={i} style={style} />)}

      {/* Radial glow effects */}
      <div className="absolute top-[15%] left-[10%] w-[400px] h-[400px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(245,168,0,0.1) 0%, transparent 70%)' }} />
      <div className="absolute bottom-[10%] right-[5%] w-[350px] h-[350px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(0,100,200,0.08) 0%, transparent 70%)' }} />

      {/* Login card */}
      <div className={`relative z-10 w-full max-w-md mx-4 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        {/* Logo */}
        <div className={`flex flex-col items-center mb-8 transition-all duration-700 delay-200 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
          <div className="w-28 h-28 mb-4 rounded-2xl flex items-center justify-center"
            style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <img src="/tw1-logo.png" alt="Truckwash 1 Group" className="w-24 h-24 object-contain" />
          </div>
          <h1 className="text-white text-2xl font-bold tracking-tight">Truckwash 1 Group</h1>
          <p className="text-white/50 text-sm mt-1">Welkom bij TW1 Administratie Portaal</p>
        </div>

        {/* Form card */}
        <div className={`rounded-2xl p-8 transition-all duration-700 delay-400 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          style={{
            background: 'rgba(255,255,255,0.06)',
            backdropFilter: 'blur(24px)',
            border: '1px solid rgba(255,255,255,0.1)',
            boxShadow: '0 24px 48px rgba(0,0,0,0.3)',
          }}
        >
          <h2 className="text-white text-xl font-semibold mb-1">Inloggen</h2>
          <p className="text-white/40 text-sm mb-6">Voer je gegevens in om door te gaan</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-white/70 text-sm">E-mailadres</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  data-testid="login-email-input"
                  type="email"
                  placeholder="naam@truckwash1group.nl"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12 pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/25 focus:border-[#f5a800]/50 focus:ring-[#f5a800]/20 rounded-xl"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-white/70 text-sm">Wachtwoord</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  data-testid="login-password-input"
                  type="password"
                  value={wachtwoord}
                  onChange={(e) => setWachtwoord(e.target.value)}
                  required
                  className="h-12 pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/25 focus:border-[#f5a800]/50 focus:ring-[#f5a800]/20 rounded-xl"
                />
              </div>
            </div>

            {needs2fa && (
              <div className="space-y-2">
                <Label className="text-white/70 text-sm flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5" /> Verificatiecode (2FA)
                </Label>
                <div className="relative">
                  <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <Input
                    data-testid="login-totp-input"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={8}
                    placeholder="000000"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                    autoFocus
                    className="h-12 pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/25 focus:border-[#f5a800]/50 focus:ring-[#f5a800]/20 rounded-xl text-center tracking-[0.3em] font-mono text-lg"
                  />
                </div>
                <p className="text-white/30 text-[11px]">Voer de 6-cijferige code in van je authenticator app of een backup code</p>
              </div>
            )}

            {error && (
              <div data-testid="login-error-text" className="flex items-center gap-2 text-sm p-3 rounded-xl"
                style={{ background: 'rgba(220,38,38,0.15)', border: '1px solid rgba(220,38,38,0.2)', color: '#fca5a5' }}>
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full h-12 rounded-xl text-sm font-semibold transition-all duration-300 group"
              style={{
                background: loading ? 'rgba(245,168,0,0.3)' : 'linear-gradient(135deg, #f5a800 0%, #e09500 100%)',
                color: '#001a2e',
                boxShadow: loading ? 'none' : '0 4px 20px rgba(245,168,0,0.3)',
              }}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                  Bezig...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Inloggen
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </span>
              )}
            </Button>

              {/* Forgot password link */}
              <div className="text-center pt-1">
                <button
                  type="button"
                  onClick={() => { setForgotMode(true); setForgotEmail(email); setError(''); }}
                  className="text-white/40 text-xs hover:text-[#f5a800] transition-colors"
                  data-testid="forgot-password-link"
                >
                  Wachtwoord vergeten?
                </button>
              </div>
          </form>
        </div>

        {/* Forgot Password Modal */}
        {forgotMode && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => { setForgotMode(false); setForgotSent(false); }}>
            <div className="w-full max-w-sm rounded-2xl overflow-hidden" onClick={e => e.stopPropagation()}
              style={{ background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <div className="p-6">
                {forgotSent ? (
                  <div className="text-center space-y-3" data-testid="forgot-sent">
                    <div className="w-12 h-12 rounded-full bg-[#1e8c45]/20 flex items-center justify-center mx-auto">
                      <Mail className="w-6 h-6 text-[#1e8c45]" />
                    </div>
                    <h3 className="text-white font-semibold">E-mail verstuurd</h3>
                    <p className="text-white/50 text-sm">Als dit e-mailadres bekend is, ontvang je een reset-link. Controleer je inbox.</p>
                    <Button onClick={() => { setForgotMode(false); setForgotSent(false); }} variant="outline" className="text-white border-white/20 hover:bg-white/5 mt-2">
                      Terug naar inloggen
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <h3 className="text-white font-semibold text-lg">Wachtwoord vergeten?</h3>
                    <p className="text-white/50 text-sm">Voer je e-mailadres in en we sturen een reset-link.</p>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                      <Input
                        type="email"
                        value={forgotEmail}
                        onChange={e => setForgotEmail(e.target.value)}
                        placeholder="naam@truckwash1group.nl"
                        required
                        className="h-12 pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/25 rounded-xl"
                        data-testid="forgot-email-input"
                      />
                    </div>
                    <Button type="submit" disabled={forgotLoading} className="w-full h-10 rounded-xl" style={{ background: 'linear-gradient(135deg, #f5a800 0%, #e09500 100%)', color: '#001a2e' }} data-testid="forgot-submit-btn">
                      {forgotLoading ? 'Verzenden...' : 'Reset-link versturen'}
                    </Button>
                    <button type="button" onClick={() => setForgotMode(false)} className="w-full text-white/40 text-xs hover:text-white/60">
                      Terug naar inloggen
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className={`text-center mt-6 transition-all duration-700 delay-600 ${mounted ? 'opacity-100' : 'opacity-0'}`}>
          <p className="text-white/25 text-xs">Beveiligd portaal &mdash; Alleen voor geautoriseerd personeel</p>
        </div>
      </div>

      <style>{`
        .login-bubble {
          position: absolute;
          border-radius: 50%;
          background: radial-gradient(circle at 30% 30%, rgba(245,168,0,0.15), rgba(0,51,102,0.1));
          animation: loginBubbleRise linear infinite;
          opacity: 0;
          pointer-events: none;
        }
        @keyframes loginBubbleRise {
          0% { opacity: 0; transform: translateY(0) scale(0.4); }
          10% { opacity: 0.5; }
          60% { opacity: 0.2; }
          100% { opacity: 0; transform: translateY(-100vh) scale(1); }
        }
      `}</style>
    </div>
  );
}
